import { useLocation } from '@packages/router-dom';
import { useEffect, useRef } from 'react';
import { useTopProgressBarProvider } from '../../../components/TopProgressBar';
import isNull from 'lodash/isNull';
import { useAPIRouteCancellation } from '../network/hooks';
import { useListProviderSyncStateInitialization } from '../../list/context/ListProvider/hooks/useListProviderSyncStateInitialization';
import { useUserPageContextAPI } from '../../user';
import { useStyleContextAPI } from '../../styleContext';
import { useTranslationAPI } from '../../translations';
import { useIsNewModule } from '../../../hooks/useIsNewModule';
import { useDataIsFetching } from '@packages/dataQuery';
import { matchesListPathName } from '../../../hooks/useIsListPage';
import { scrollTo } from '../../../utils/screen';
import { useRecentlyViewedRemoveQueryParamsMigration } from '../../../components/RecentlyViewed/hooks/useRecentlyViewedRemoveQueryParamsMigration';

function useAppLocationScrollToTop() {
	const { pathname } = useLocation();

	// temp solution
	useEffect(() => {
		const isListPage = matchesListPathName(pathname);

		if (isListPage) {
			return;
		}

		setTimeout(() => {
			scrollTo({
				y: 0,
			});
		}, 250);
	}, [pathname]);
}

function useQueryTopProgressBar() {
	const { start, finish } = useTopProgressBarProvider();
	const lastProgressIdRef = useRef<number | null>(null);
	const isProgressPendingRef = useRef(false);
	const fetchingQueriesNumber = useDataIsFetching();

	useEffect(() => {
		const shouldStartProgress = fetchingQueriesNumber > 0 && !isProgressPendingRef.current;
		const shouldFinishProgress = fetchingQueriesNumber === 0 && isProgressPendingRef.current;

		const startProgress = () => {
			lastProgressIdRef.current = start();
			isProgressPendingRef.current = true;
		};

		const finishProgress = () => {
			if (isNull(lastProgressIdRef.current)) return;

			finish(lastProgressIdRef.current);
			isProgressPendingRef.current = false;
		};

		if (shouldStartProgress) {
			startProgress();
		}

		if (shouldFinishProgress) {
			finishProgress();
		}
	}, [fetchingQueriesNumber, finish, start]);
}

function useAppLocationHandler() {
	const { pathname } = useLocation();
	const { tryFetchUserContext } = useUserPageContextAPI();
	const { tryFetchStyleContext } = useStyleContextAPI();
	const { tryFetchTranslations } = useTranslationAPI();
	const isNewModule = useIsNewModule(pathname);

	useEffect(() => {
		if (isNewModule) {
			tryFetchUserContext();
			tryFetchStyleContext();
		}

		tryFetchTranslations();
	}, [isNewModule, tryFetchStyleContext, tryFetchTranslations, tryFetchUserContext]);
}

function useAppHooks() {
	useAppLocationScrollToTop();
	useAppLocationHandler();
	useAPIRouteCancellation();
	useListProviderSyncStateInitialization();
	useQueryTopProgressBar();
	useRecentlyViewedRemoveQueryParamsMigration({
		queryParamsToRemove: ['tab'],
	});
}

export { useAppHooks };
